import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { recruiterAPI, screeningAPI, interviewJourneyAPI, walletAPI } from '@/services/api';
import { ArrowLeft, Users, Zap, Play, CheckCircle, Clock, Edit2, Loader2, Settings, Mic, FileText, Brain, ChevronDown, ChevronUp, Plus, Trash2, Download, Video, Sparkles, X } from 'lucide-react';
import ConfirmChargeDialog from '@/components/shared/ConfirmChargeDialog';

export default function RecruiterJobDetail() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [matching, setMatching] = useState(false);
  const [scheduling, setScheduling] = useState({});
  const [showJourneyStart, setShowJourneyStart] = useState(false);
  const [journeyMode, setJourneyMode] = useState('selective');
  const [selectedMatches, setSelectedMatches] = useState([]);
  const [walletInfo, setWalletInfo] = useState({ balance: 0, pricing: { candidate_journey: 3 } });
  const [confirmStartOpen, setConfirmStartOpen] = useState(false);
  const [journeyResults, setJourneyResults] = useState(null);
  // Screening config state
  const [showConfig, setShowConfig] = useState(false);
  const [screeningEnabled, setScreeningEnabled] = useState(false);
  const [questionCount, setQuestionCount] = useState(5);
  const [questionSource, setQuestionSource] = useState('ai_suggested');
  const [manualQuestions, setManualQuestions] = useState([]);
  const [suggestedQuestions, setSuggestedQuestions] = useState([]);
  const [evaluationEnabled, setEvaluationEnabled] = useState(true);
  const [evaluationMode, setEvaluationMode] = useState('stt_evaluate'); // 'audio_only' | 'stt_evaluate'
  const [savingConfig, setSavingConfig] = useState(false);
  const [suggestLoading, setSuggestLoading] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [j, m] = await Promise.all([recruiterAPI.getJob(jobId), recruiterAPI.getJobMatches(jobId)]);
        setJob(j);
        setMatches(m);
        // Load existing screening config
        const sc = j.ai_screening || {};
        setScreeningEnabled(sc.enabled || false);
        setQuestionCount(sc.question_count || 5);
        setQuestionSource(sc.question_source || 'ai_suggested');
        setEvaluationEnabled(sc.evaluation_enabled !== false);
        setEvaluationMode(sc.evaluation_mode || 'stt_evaluate');
        if (sc.questions?.length) {
          if (sc.question_source === 'manual') setManualQuestions(sc.questions);
          else setSuggestedQuestions(sc.questions);
        }
      } catch (e) { alert(e.response?.data?.detail || e.message); }
      finally { setLoading(false); }
    };
    load();
  }, [jobId]);

  const triggerMatch = async () => {
    setMatching(true);
    try { const r = await recruiterAPI.triggerMatching(jobId); alert(r.message); setMatches(await recruiterAPI.getJobMatches(jobId)); }
    catch (e) { alert(e.response?.data?.detail || e.message); }
    setMatching(false);
  };

  const scheduleScreening = async (matchId) => {
    setScheduling(prev => ({ ...prev, [matchId]: true }));
    try { await screeningAPI.scheduleScreening(matchId); setMatches(await recruiterAPI.getJobMatches(jobId)); }
    catch (e) { alert(e.response?.data?.detail || e.message); }
    setScheduling(prev => ({ ...prev, [matchId]: false }));
  };

  const saveScreeningConfig = async () => {
    setSavingConfig(true);
    try {
      const questions = questionSource === 'manual' ? manualQuestions : suggestedQuestions;
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('auth_token');
      await fetch(`${API_URL}/api/jobs/${jobId}/screening-config`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          enabled: screeningEnabled, questionCount, questionSource, questions,
          evaluationEnabled, evaluationMode,
        })
      });
      alert('Screening configuration saved!');
    } catch (e) { alert('Error saving config'); }
    setSavingConfig(false);
  };

  const [toughness, setToughness] = useState(5);

  const suggestQuestions = async () => {
    setSuggestLoading(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('auth_token');
      const r = await fetch(`${API_URL}/api/jobs/${jobId}/suggest-questions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ count: questionCount, toughness })
      });
      const data = await r.json();
      setSuggestedQuestions(data.questions || []);
      if (data.questions?.length) setQuestionSource('ai_suggested');
    } catch { alert('Failed to generate questions'); }
    setSuggestLoading(false);
  };

  const addManualQuestion = () => setManualQuestions(prev => [...prev, { id: String(prev.length), text: '', order: prev.length }]);
  const removeManualQuestion = (i) => setManualQuestions(prev => prev.filter((_, idx) => idx !== i));
  const updateManualQuestion = (i, text) => setManualQuestions(prev => prev.map((q, idx) => idx === i ? { ...q, text } : q));

  // Bug #3 — editing helpers for the AI-suggested list so recruiters can add / rewrite / remove
  const [rewritingIdx, setRewritingIdx] = useState(null);
  const addSuggestedQuestion = () => setSuggestedQuestions(prev => [
    ...prev,
    { id: `custom-${prev.length}`, text: '', order: prev.length, category: 'custom', difficulty: 'medium', custom: true },
  ]);
  const removeSuggestedQuestion = (i) => setSuggestedQuestions(prev => prev.filter((_, idx) => idx !== i));
  const updateSuggestedQuestion = (i, text) => setSuggestedQuestions(prev => prev.map((q, idx) => idx === i ? { ...q, text } : q));
  const rewriteSuggestedQuestion = async (i) => {
    const q = suggestedQuestions[i];
    if (!q?.text?.trim()) { alert('Write or generate a question first.'); return; }
    setRewritingIdx(i);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('auth_token');
      const r = await fetch(`${API_URL}/api/jobs/${jobId}/rewrite-question`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ text: q.text, toughness }),
      });
      const data = await r.json();
      if (data?.text) updateSuggestedQuestion(i, data.text);
    } catch { alert('Failed to rewrite question'); }
    setRewritingIdx(null);
  };

  if (loading) return <p className="text-slate-500 dark:text-zinc-500">Loading job...</p>;
  if (!job) return <p className="text-red-600 dark:text-red-400">Job not found</p>;

  const statusColors = {
    matched: 'bg-blue-500/10 text-blue-600 dark:text-blue-400', screening_scheduled: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
    screening_completed: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400', selected: 'bg-green-500/10 text-green-600 dark:text-green-400', rejected: 'bg-red-500/10 text-red-600 dark:text-red-400',
  };

  return (
    <div data-testid="recruiter-job-detail">
      <button onClick={() => navigate('/recruiter/jobs')} className="flex items-center gap-1.5 text-slate-500 dark:text-zinc-500 text-sm mb-4 hover:text-slate-600 dark:hover:text-zinc-300" data-testid="back-to-jobs">
        <ArrowLeft className="w-4 h-4" /> Back to Jobs
      </button>

      {/* Job Header */}
      <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-lg p-6 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <h1 className="text-2xl font-semibold text-slate-900 dark:text-white">{job.title}</h1>
              <span className={`text-xs px-2 py-0.5 rounded ${job.status === 'active' ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' : 'bg-slate-100 dark:bg-zinc-800 text-slate-500 dark:text-zinc-500'}`}>{job.status}</span>
            </div>
            <p className="text-slate-500 dark:text-zinc-400 text-sm mb-3">{job.description}</p>
            <div className="flex flex-wrap gap-4 text-xs text-slate-500 dark:text-zinc-500">
              {job.compensation_min && <span>${job.compensation_min?.toLocaleString()} - ${job.compensation_max?.toLocaleString()} {job.currency}</span>}
              <span>Min Match: {job.min_match_percentage}%</span>
              {job.job_fit_threshold && Number(job.job_fit_threshold) > 0 && (
                <span data-testid="job-fit-badge" className="text-emerald-700 dark:text-emerald-400">
                  AI auto-decision · fit ≥ {Number(job.job_fit_threshold)}%
                </span>
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-2 sm:flex-shrink-0 min-w-0">
            <button onClick={() => setShowConfig(!showConfig)}
              className={`px-3 py-2 rounded-lg text-sm flex items-center gap-1.5 transition-colors ${showConfig ? 'bg-blue-600 text-white' : 'bg-slate-100 dark:bg-zinc-800 text-slate-600 dark:text-zinc-300 hover:bg-slate-200 dark:hover:bg-zinc-700'}`}
              data-testid="screening-config-btn">
              <Settings className="w-3.5 h-3.5" /> AI Screening Setup
              {showConfig ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </button>
            <button onClick={() => navigate(`/recruiter/jobs/${jobId}/journey`)} className="px-3 py-2 bg-slate-100 dark:bg-zinc-800 text-slate-600 dark:text-zinc-300 rounded-lg text-sm hover:bg-slate-200 dark:hover:bg-zinc-700 flex items-center gap-1.5">
              <Users className="w-3.5 h-3.5" /> Journey View
            </button>
            <button onClick={() => navigate(`/recruiter/jobs/${jobId}/interviews`)} className="px-3 py-2 bg-purple-600/20 text-purple-700 dark:text-purple-300 border border-purple-500/30 rounded-lg text-sm hover:bg-purple-600/30 flex items-center gap-1.5" data-testid="review-interviews-btn">
              <Video className="w-3.5 h-3.5" /> Review Interviews
            </button>
            <button onClick={() => setShowJourneyStart(true)} className="px-3 py-2 bg-emerald-600/20 text-emerald-700 dark:text-emerald-300 border border-emerald-500/30 rounded-lg text-sm hover:bg-emerald-600/30 flex items-center gap-1.5" data-testid="start-journeys-btn">
              <Sparkles className="w-3.5 h-3.5" /> Start Interview Journey
            </button>
          </div>
        </div>
      </div>

      {/* ===== AI SCREENING CONFIG PANEL (Fix 3) ===== */}
      {showConfig && (
        <div className="bg-white dark:bg-[#111114] border border-blue-500/20 rounded-lg p-6 mb-6" data-testid="screening-config-panel">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-1 flex items-center gap-2"><Brain className="w-5 h-5 text-blue-600 dark:text-blue-400" /> AI Screening by Jay</h2>
          <p className="text-slate-500 dark:text-zinc-500 text-sm mb-5">Configure how Jay conducts screening interviews for this job</p>

          {/* Enable toggle */}
          <div className="flex items-center justify-between mb-5 pb-4 border-b border-slate-200 dark:border-zinc-800/50">
            <div>
              <p className="text-slate-900 dark:text-white text-sm font-medium">Enable AI Screening</p>
              <p className="text-slate-500 dark:text-zinc-600 text-xs">Jay will interview candidates with your configured questions</p>
            </div>
            <button onClick={() => setScreeningEnabled(!screeningEnabled)}
              className={`w-12 h-6 rounded-full transition-colors ${screeningEnabled ? 'bg-blue-500' : 'bg-slate-200 dark:bg-zinc-700'}`} data-testid="screening-toggle">
              <div className={`w-5 h-5 rounded-full bg-white transition-transform ${screeningEnabled ? 'translate-x-6' : 'translate-x-0.5'}`} />
            </button>
          </div>

          {screeningEnabled && (
            <div className="space-y-5">
              {/* Question count */}
              <div>
                <label className="text-sm text-slate-500 dark:text-zinc-400 mb-2 block">Number of questions</label>
                <div className="flex gap-2">
                  {[3, 5, 7, 10].map(n => (
                    <button key={n} onClick={() => setQuestionCount(n)}
                      className={`px-4 py-2 rounded-lg text-sm transition-colors ${questionCount === n ? 'bg-blue-600 text-white' : 'bg-slate-100 dark:bg-zinc-800 text-slate-500 dark:text-zinc-400 hover:bg-slate-200 dark:hover:bg-zinc-700'}`}
                      data-testid={`q-count-${n}`}>{n}</button>
                  ))}
                </div>
              </div>

              {/* Question source */}
              <div>
                <label className="text-sm text-slate-500 dark:text-zinc-400 mb-2 block">Question source</label>
                <div className="grid grid-cols-2 gap-3">
                  <button onClick={() => setQuestionSource('manual')}
                    className={`p-3 rounded-lg border text-left transition-all ${questionSource === 'manual' ? 'border-blue-500/50 bg-blue-500/5' : 'border-slate-200 dark:border-zinc-800 bg-slate-50 dark:bg-zinc-900 hover:border-slate-300 dark:hover:border-zinc-700'}`}>
                    <FileText className={`w-4 h-4 mb-1 ${questionSource === 'manual' ? 'text-blue-600 dark:text-blue-400' : 'text-slate-500 dark:text-zinc-500'}`} />
                    <p className={`text-sm font-medium ${questionSource === 'manual' ? 'text-blue-700 dark:text-blue-300' : 'text-slate-600 dark:text-zinc-300'}`}>I&apos;ll provide questions</p>
                    <p className="text-xs text-slate-500 dark:text-zinc-600">Write your own screening questions</p>
                  </button>
                  <button onClick={() => setQuestionSource('ai_suggested')}
                    className={`p-3 rounded-lg border text-left transition-all ${questionSource === 'ai_suggested' ? 'border-blue-500/50 bg-blue-500/5' : 'border-slate-200 dark:border-zinc-800 bg-slate-50 dark:bg-zinc-900 hover:border-slate-300 dark:hover:border-zinc-700'}`}>
                    <Brain className={`w-4 h-4 mb-1 ${questionSource === 'ai_suggested' ? 'text-blue-600 dark:text-blue-400' : 'text-slate-500 dark:text-zinc-500'}`} />
                    <p className={`text-sm font-medium ${questionSource === 'ai_suggested' ? 'text-blue-700 dark:text-blue-300' : 'text-slate-600 dark:text-zinc-300'}`}>Let Jay suggest</p>
                    <p className="text-xs text-slate-500 dark:text-zinc-600">AI generates questions from your JD</p>
                  </button>
                </div>
              </div>

              {/* Manual questions */}
              {questionSource === 'manual' && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm text-slate-500 dark:text-zinc-400">Your questions</label>
                    <button onClick={addManualQuestion} className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 flex items-center gap-1"><Plus className="w-3 h-3" /> Add</button>
                  </div>
                  <div className="space-y-2">
                    {manualQuestions.map((q, i) => (
                      <div key={i} className="flex gap-2">
                        <span className="text-slate-500 dark:text-zinc-600 text-sm pt-2 w-6">Q{i + 1}</span>
                        <input value={q.text} onChange={e => updateManualQuestion(i, e.target.value)} placeholder={`Question ${i + 1}...`}
                          className="flex-1 bg-slate-50 dark:bg-zinc-900 border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-zinc-600 focus:outline-none focus:border-slate-300 dark:focus:border-zinc-600" />
                        <button onClick={() => removeManualQuestion(i)} className="p-2 text-slate-500 dark:text-zinc-600 hover:text-red-600 dark:hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    ))}
                    {manualQuestions.length === 0 && <p className="text-slate-500 dark:text-zinc-600 text-xs">Click &quot;+ Add&quot; to write your questions</p>}
                  </div>
                </div>
              )}

              {/* AI suggested questions */}
              {questionSource === 'ai_suggested' && (
                <div>
                  {/* Toughness slider */}
                  <div className="mb-4 bg-slate-50 dark:bg-zinc-900/50 border border-slate-200 dark:border-zinc-800 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm text-slate-500 dark:text-zinc-400">Toughness Level</label>
                      <span className="text-sm font-mono font-bold" style={{color: toughness <= 3 ? '#22c55e' : toughness <= 6 ? '#eab308' : '#ef4444'}}>{toughness}/10</span>
                    </div>
                    <input type="range" min="0" max="10" value={toughness} onChange={e => setToughness(Number(e.target.value))}
                      className="w-full h-1.5 bg-slate-100 dark:bg-zinc-800 rounded-lg appearance-none cursor-pointer accent-blue-500" data-testid="toughness-slider" />
                    <div className="flex justify-between mt-1">
                      <span className="text-[10px] text-slate-500 dark:text-zinc-600">Easy</span>
                      <span className="text-[10px] text-slate-500 dark:text-zinc-600">Moderate</span>
                      <span className="text-[10px] text-slate-500 dark:text-zinc-600">Expert</span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm text-slate-500 dark:text-zinc-400">AI-Generated Questions</label>
                    <div className="flex items-center gap-2">
                      <button onClick={addSuggestedQuestion}
                        className="text-xs text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300 flex items-center gap-1" data-testid="add-suggested-question-btn">
                        <Plus className="w-3 h-3" /> Add my own
                      </button>
                      <button onClick={suggestQuestions} disabled={suggestLoading}
                        className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 flex items-center gap-1 disabled:opacity-50" data-testid="suggest-questions-btn">
                        {suggestLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Brain className="w-3 h-3" />}
                        {suggestLoading ? 'Generating...' : suggestedQuestions.length ? 'Regenerate' : 'Generate Questions'}
                      </button>
                    </div>
                  </div>
                  {suggestedQuestions.length > 0 ? (
                    <div className="space-y-2">
                      {suggestedQuestions.map((q, i) => (
                        <div key={i} className="bg-slate-50 dark:bg-zinc-900 border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-2.5" data-testid={`suggested-question-row-${i}`}>
                          <div className="flex items-start gap-2">
                            <span className="text-slate-500 dark:text-zinc-600 text-xs mt-2">Q{i + 1}</span>
                            <div className="flex-1">
                              <textarea
                                value={q.text}
                                onChange={e => updateSuggestedQuestion(i, e.target.value)}
                                rows={2}
                                placeholder="Type your question or click Rewrite to let AI polish it…"
                                className="w-full bg-white dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-md px-2 py-1.5 text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-zinc-600 focus:outline-none focus:border-slate-300 dark:focus:border-zinc-600"
                                data-testid={`suggested-question-input-${i}`}
                              />
                              <div className="flex items-center gap-2 mt-1 flex-wrap">
                                {q.category && <span className="text-slate-500 dark:text-zinc-600 text-xs capitalize">{q.category}</span>}
                                {q.difficulty && <span className={`text-[10px] px-1.5 py-0.5 rounded ${q.difficulty === 'hard' ? 'bg-red-500/10 text-red-600 dark:text-red-400' : q.difficulty === 'medium' ? 'bg-amber-500/10 text-amber-600 dark:text-amber-400' : 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'}`}>{q.difficulty}</span>}
                                {q.custom && <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-600 dark:text-blue-400">custom</span>}
                              </div>
                            </div>
                            <div className="flex flex-col gap-1">
                              <button
                                type="button"
                                onClick={() => rewriteSuggestedQuestion(i)}
                                disabled={rewritingIdx === i}
                                title="Rewrite with AI"
                                className="p-1.5 text-slate-500 dark:text-zinc-500 hover:text-blue-600 dark:hover:text-blue-400 disabled:opacity-50"
                                data-testid={`rewrite-question-${i}`}
                              >
                                {rewritingIdx === i ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                              </button>
                              <button
                                type="button"
                                onClick={() => removeSuggestedQuestion(i)}
                                title="Remove"
                                className="p-1.5 text-slate-500 dark:text-zinc-500 hover:text-red-600 dark:hover:text-red-400"
                                data-testid={`remove-suggested-question-${i}`}
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : <p className="text-slate-500 dark:text-zinc-600 text-xs">Click &quot;Generate Questions&quot; to let Jay create questions from your job description, or &quot;Add my own&quot; to write your own.</p>}
                </div>
              )}

              {/* Evaluation settings */}
              <div className="pt-4 border-t border-slate-200 dark:border-zinc-800/50">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-slate-900 dark:text-white text-sm font-medium">Candidate Evaluation (JF Score)</p>
                    <p className="text-slate-500 dark:text-zinc-600 text-xs">Auto-evaluate answers and produce a JobFit percentage</p>
                  </div>
                  <button onClick={() => setEvaluationEnabled(!evaluationEnabled)}
                    className={`w-12 h-6 rounded-full transition-colors ${evaluationEnabled ? 'bg-emerald-500' : 'bg-slate-200 dark:bg-zinc-700'}`} data-testid="evaluation-toggle">
                    <div className={`w-5 h-5 rounded-full bg-white transition-transform ${evaluationEnabled ? 'translate-x-6' : 'translate-x-0.5'}`} />
                  </button>
                </div>

                {evaluationEnabled && (
                  <div>
                    <label className="text-sm text-slate-500 dark:text-zinc-400 mb-2 block">Evaluation Mode</label>
                    <div className="grid grid-cols-2 gap-3">
                      <button onClick={() => setEvaluationMode('audio_only')}
                        className={`p-3 rounded-lg border text-left transition-all ${evaluationMode === 'audio_only' ? 'border-amber-500/50 bg-amber-500/5' : 'border-slate-200 dark:border-zinc-800 bg-slate-50 dark:bg-zinc-900 hover:border-slate-300 dark:hover:border-zinc-700'}`}>
                        <Mic className={`w-4 h-4 mb-1 ${evaluationMode === 'audio_only' ? 'text-amber-600 dark:text-amber-400' : 'text-slate-500 dark:text-zinc-500'}`} />
                        <p className={`text-sm font-medium ${evaluationMode === 'audio_only' ? 'text-amber-700 dark:text-amber-300' : 'text-slate-600 dark:text-zinc-300'}`}>Audio Only</p>
                        <p className="text-xs text-slate-500 dark:text-zinc-600">Provide recordings to recruiter. No auto-scoring.</p>
                      </button>
                      <button onClick={() => setEvaluationMode('stt_evaluate')}
                        className={`p-3 rounded-lg border text-left transition-all ${evaluationMode === 'stt_evaluate' ? 'border-emerald-500/50 bg-emerald-500/5' : 'border-slate-200 dark:border-zinc-800 bg-slate-50 dark:bg-zinc-900 hover:border-slate-300 dark:hover:border-zinc-700'}`}>
                        <Brain className={`w-4 h-4 mb-1 ${evaluationMode === 'stt_evaluate' ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-500 dark:text-zinc-500'}`} />
                        <p className={`text-sm font-medium ${evaluationMode === 'stt_evaluate' ? 'text-emerald-700 dark:text-emerald-300' : 'text-slate-600 dark:text-zinc-300'}`}>STT + JF Evaluation</p>
                        <p className="text-xs text-slate-500 dark:text-zinc-600">Convert speech to text, AI scores JobFit %</p>
                      </button>
                    </div>
                    <p className="text-slate-500 dark:text-zinc-600 text-xs mt-2">
                      {evaluationMode === 'stt_evaluate'
                        ? 'Jay will transcribe audio answers via Speech-to-Text, then evaluate against your JD to produce a per-question and overall JF% score.'
                        : 'Candidate audio recordings will be provided directly to you. You evaluate manually.'}
                    </p>
                  </div>
                )}
              </div>

              {/* Save button */}
              <button onClick={saveScreeningConfig} disabled={savingConfig}
                className="w-full py-2.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-500 disabled:opacity-50 flex items-center justify-center gap-2" data-testid="save-screening-config">
                {savingConfig ? <Loader2 className="w-4 h-4 animate-spin" /> : <Settings className="w-4 h-4" />}
                {savingConfig ? 'Saving...' : 'Save Screening Configuration'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* ===== AUTO-INTERVIEW STATUS BANNER (auto mode only) ===== */}
      {job.candidate_selection_mode === 'auto' && (
        <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-lg p-4 mb-4" data-testid="auto-interview-banner">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-slate-900 dark:text-white text-sm font-medium">
                Auto-interview budget · <span data-testid="auto-spent">{job.auto_interview_spent_mp || 0}</span>
                {' '}/ <span data-testid="auto-budget">{job.auto_interview_budget_mp ?? '∞'}</span> MP spent
              </p>
              {job.auto_interview_paused ? (
                <p className="text-amber-700 dark:text-amber-300 text-xs mt-0.5" data-testid="auto-pause-reason">
                  {job.auto_interview_pause_reason === 'budget_reached'
                    ? 'Budget cap reached. Increase the budget in job settings or use manual override to continue interviewing more candidates.'
                    : 'Auto-scheduling paused — insufficient Mu Points. Recharge your wallet, then click Continue.'}
                </p>
              ) : (
                <p className="text-slate-500 dark:text-zinc-500 text-xs mt-0.5">
                  Ready to auto-schedule matched candidates in match% order until the budget is reached.
                </p>
              )}
            </div>
            <button
              onClick={async () => {
                try {
                  const r = await interviewJourneyAPI.startJourneys(jobId, 'auto', []);
                  setMatches(await recruiterAPI.getJobMatches(jobId));
                  setJob(await recruiterAPI.getJob(jobId));
                  const paused = r?.auto_interview_paused;
                  alert(`Started ${r.started} · Skipped ${r.skipped} · Spent ${r.total_charged} MP${paused ? ` · Paused: ${r.auto_interview_pause_reason}` : ''}`);
                } catch (e) { alert(e?.response?.data?.detail || 'Failed to continue auto-scheduling'); }
              }}
              className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-sm font-medium flex items-center gap-1.5 self-start sm:self-auto"
              data-testid="auto-continue-btn"
            >
              <Sparkles className="w-4 h-4" />
              {job.auto_interview_paused ? 'Continue auto-scheduling' : 'Run auto-schedule now'}
            </button>
          </div>
        </div>
      )}

      {/* Matches Section */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div>
          <h2 className="text-lg font-medium text-slate-900 dark:text-white">Matched Candidates</h2>
          <p className="text-slate-500 dark:text-zinc-500 text-xs">{matches.length} candidates matched</p>
        </div>
        <button onClick={triggerMatch} disabled={matching} className="flex items-center gap-2 px-4 py-2 bg-slate-900 dark:bg-white text-white dark:text-black rounded-lg text-sm disabled:opacity-50 self-start sm:self-auto" data-testid="trigger-match-btn">
          {matching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
          {matching ? 'Matching...' : 'Run AI Matching'}
        </button>
      </div>

      {matches.length === 0 ? (
        <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-lg p-10 text-center">
          <Users className="w-10 h-10 text-slate-600 dark:text-zinc-700 mx-auto mb-3" />
          <p className="text-slate-500 dark:text-zinc-500">No matches yet. Click &quot;Run AI Matching&quot; to find candidates.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {matches.map(match => (
            <div key={match.id} className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-lg p-4" data-testid={`match-${match.id}`}>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <span className={`text-lg font-bold ${match.match_percentage >= 80 ? 'text-emerald-600 dark:text-emerald-400' : match.match_percentage >= 60 ? 'text-amber-600 dark:text-amber-400' : 'text-slate-500 dark:text-zinc-400'}`}>
                      {Math.round(match.match_percentage)}%
                    </span>
                    <span className={`text-xs px-2 py-0.5 rounded capitalize ${statusColors[match.status] || 'bg-slate-100 dark:bg-zinc-800 text-slate-500 dark:text-zinc-400'}`}>{match.status?.replace('_', ' ')}</span>
                  </div>
                  <p className="text-slate-900 dark:text-white text-sm font-medium truncate" data-testid={`match-name-${match.id}`}>
                    {match.applicant_name || 'Candidate'}
                  </p>
                  {match.applicant_email && (
                    <p className="text-slate-500 dark:text-zinc-500 text-xs truncate" data-testid={`match-email-${match.id}`}>
                      {match.applicant_email}
                    </p>
                  )}
                  {match.match_tags?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {match.match_tags.map((t, i) => <span key={i} className="px-2 py-0.5 bg-slate-100 dark:bg-zinc-800 text-slate-500 dark:text-zinc-400 rounded text-xs">{t.category}: {Math.round(t.score)}%</span>)}
                    </div>
                  )}
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  {match.status === 'matched' && job.candidate_selection_mode !== 'auto' && (
                    <button onClick={() => scheduleScreening(match.id)} disabled={scheduling[match.id]}
                      className="flex items-center gap-1.5 px-3 py-2 bg-emerald-600 text-white rounded-lg text-sm hover:bg-emerald-500 disabled:opacity-50"
                      data-testid={`schedule-screening-${match.id}`}>
                      {scheduling[match.id] ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />} Schedule
                    </button>
                  )}
                  {match.status === 'matched' && job.candidate_selection_mode === 'auto' && job.auto_interview_paused && (
                    <span className="flex items-center gap-1.5 text-amber-600 dark:text-amber-400 text-sm" data-testid={`awaiting-continue-${match.id}`}>
                      <Clock className="w-3.5 h-3.5" /> Awaiting continue
                    </span>
                  )}
                  {match.status === 'screening_scheduled' && <span className="flex items-center gap-1.5 text-amber-600 dark:text-amber-400 text-sm"><Clock className="w-3.5 h-3.5" /> Scheduled</span>}
                  {match.status === 'screening_completed' && <span className="flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400 text-sm"><CheckCircle className="w-3.5 h-3.5" /> Done</span>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ===== START INTERVIEW JOURNEY MODAL ===== */}
      {showJourneyStart && (
        <StartJourneyModal
          matches={matches}
          jobId={jobId}
          mode={journeyMode}
          setMode={setJourneyMode}
          selected={selectedMatches}
          setSelected={setSelectedMatches}
          walletInfo={walletInfo}
          setWalletInfo={setWalletInfo}
          onClose={() => { setShowJourneyStart(false); setSelectedMatches([]); }}
          onConfirm={() => setConfirmStartOpen(true)}
        />
      )}

      <ConfirmChargeDialog
        open={confirmStartOpen}
        onClose={() => setConfirmStartOpen(false)}
        title={journeyMode === 'auto' ? 'Auto-start ALL matches?' : 'Start journey for selected candidates?'}
        description={journeyMode === 'auto' ? 'Every eligible match will be auto-advanced. Charges proceed even if your balance goes negative.' : `${selectedMatches.length} candidate(s) selected.`}
        cost={(walletInfo.pricing?.candidate_journey || 3) * (journeyMode === 'auto' ? Math.max(matches.filter(m => ['matched','interested','viewed'].includes(m.status)).length, 0) : selectedMatches.length)}
        balance={walletInfo.balance}
        allowNegative={journeyMode === 'auto'}
        confirmLabel="Start journeys"
        onConfirm={async () => {
          const ids = journeyMode === 'auto' ? [] : selectedMatches;
          const r = await interviewJourneyAPI.startJourneys(jobId, journeyMode, ids);
          window.dispatchEvent(new Event('mu-wallet-changed'));
          setJourneyResults(r);
          setShowJourneyStart(false);
          setSelectedMatches([]);
          const m = await recruiterAPI.getJobMatches(jobId);
          setMatches(m);
        }}
      />

      {journeyResults && (
        <JourneyResultsModal results={journeyResults} onClose={() => setJourneyResults(null)} />
      )}
    </div>
  );
}

function JourneyResultsModal({ results, onClose }) {
  const started = (results.results || []).filter(r => r.status === 'started');
  const skipped = (results.results || []).filter(r => r.status === 'skipped');
  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="journey-results-modal">
      <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800 rounded-xl w-full max-w-2xl max-h-[88vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="p-5 border-b border-slate-200 dark:border-zinc-800 flex items-center justify-between">
          <div>
            <h2 className="text-slate-900 dark:text-white font-semibold text-lg flex items-center gap-2"><Sparkles className="w-4 h-4 text-emerald-600 dark:text-emerald-400" /> Journey-start results</h2>
            <p className="text-slate-500 dark:text-zinc-500 text-xs mt-0.5">
              <span className="text-emerald-700 dark:text-emerald-300">{results.started} started</span> · <span className="text-amber-700 dark:text-amber-300">{results.skipped} skipped</span> · charged <span className="text-amber-700 dark:text-amber-300">{results.total_charged} MP</span>
              {results.balance_after !== null && results.balance_after !== undefined && <> · new balance <span className="text-slate-600 dark:text-zinc-300">{results.balance_after} MP</span></>}
            </p>
          </div>
          <button onClick={onClose} className="text-slate-500 dark:text-zinc-500 hover:text-slate-900 dark:hover:text-white" data-testid="close-journey-results"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex-1 overflow-auto p-5 space-y-4">
          {started.length > 0 && (
            <section data-testid="journey-started-section">
              <h3 className="text-xs uppercase tracking-wider text-emerald-600 dark:text-emerald-400 mb-2">Started · {started.length}</h3>
              <div className="space-y-1.5">
                {started.map(r => (
                  <div key={r.match_id} className="bg-emerald-500/5 border border-emerald-500/20 rounded-lg p-3 flex items-center justify-between text-sm" data-testid={`result-started-${r.match_id}`}>
                    <div className="min-w-0 flex-1">
                      <p className="text-slate-900 dark:text-white truncate">{r.applicant_name || r.applicant_email || `Match ${r.match_id.slice(-6)}`}</p>
                      <p className="text-slate-500 dark:text-zinc-500 text-xs">{r.applicant_email} · {Math.round(r.match_percentage || 0)}% match</p>
                    </div>
                    <span className="text-emerald-700 dark:text-emerald-300 text-xs whitespace-nowrap">−{r.charged} MP</span>
                  </div>
                ))}
              </div>
            </section>
          )}
          {skipped.length > 0 && (
            <section data-testid="journey-skipped-section">
              <h3 className="text-xs uppercase tracking-wider text-amber-600 dark:text-amber-400 mb-2">Skipped · {skipped.length}</h3>
              <div className="space-y-1.5">
                {skipped.map(r => (
                  <div key={r.match_id} className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-3 text-sm" data-testid={`result-skipped-${r.match_id}`}>
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-slate-900 dark:text-white truncate flex-1">{r.applicant_name || r.applicant_email || `Match ${r.match_id.slice(-6)}`}</p>
                      <span className="text-slate-500 dark:text-zinc-500 text-xs whitespace-nowrap">{Math.round(r.match_percentage || 0)}% match</span>
                    </div>
                    <p className="text-amber-700 dark:text-amber-300 text-xs mt-1">{r.reason}</p>
                  </div>
                ))}
              </div>
            </section>
          )}
          {started.length === 0 && skipped.length === 0 && (
            <p className="text-slate-500 dark:text-zinc-500 text-sm text-center py-6">No matches were processed.</p>
          )}
        </div>
        <div className="p-4 border-t border-slate-200 dark:border-zinc-800 flex justify-end">
          <button onClick={onClose} className="px-4 py-2 bg-slate-900 dark:bg-white text-white dark:text-black rounded-lg text-sm font-medium" data-testid="close-journey-results-btn">Done</button>
        </div>
      </div>
    </div>
  );
}

function StartJourneyModal({ matches, jobId, mode, setMode, selected, setSelected, walletInfo, setWalletInfo, onClose, onConfirm }) {
  React.useEffect(() => {
    (async () => {
      try { const w = await walletAPI.me(); setWalletInfo({ balance: w.balance, pricing: w.pricing }); }
      catch {/* ignore */}
    })();
  }, []);

  const eligible = matches.filter(m => ['matched', 'interested', 'viewed'].includes(m.status));
  const per = walletInfo.pricing?.candidate_journey || 3;
  const cost = mode === 'auto' ? eligible.length * per : selected.length * per;

  const toggle = (id) => setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const selectAll = () => setSelected(eligible.map(m => m.id));
  const selectNone = () => setSelected([]);

  return (
    <div className="fixed inset-0 bg-black/70 z-40 flex items-center justify-center p-4" onClick={onClose} data-testid="start-journey-modal">
      <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800 rounded-xl w-full max-w-2xl max-h-[88vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="p-5 border-b border-slate-200 dark:border-zinc-800 flex items-center justify-between">
          <div>
            <h2 className="text-slate-900 dark:text-white font-semibold text-lg flex items-center gap-2"><Sparkles className="w-4 h-4 text-emerald-600 dark:text-emerald-400" /> Start interview journey</h2>
            <p className="text-slate-500 dark:text-zinc-500 text-xs mt-0.5">{eligible.length} eligible match{eligible.length !== 1 && 'es'} · {per} MP per journey · balance {walletInfo.balance} MP</p>
          </div>
          <button onClick={onClose} className="text-slate-500 dark:text-zinc-500 hover:text-slate-900 dark:hover:text-white" data-testid="close-start-journey"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 border-b border-slate-200 dark:border-zinc-800 flex gap-2">
          <button onClick={() => setMode('selective')} className={`flex-1 px-3 py-2 rounded-lg text-sm border ${mode === 'selective' ? 'bg-slate-900 dark:bg-white text-white dark:text-black border-slate-900 dark:border-white' : 'bg-slate-50 dark:bg-zinc-900 text-slate-600 dark:text-zinc-300 border-slate-200 dark:border-zinc-800'}`} data-testid="journey-mode-selective">Selectively pick</button>
          <button onClick={() => setMode('auto')} className={`flex-1 px-3 py-2 rounded-lg text-sm border ${mode === 'auto' ? 'bg-slate-900 dark:bg-white text-white dark:text-black border-slate-900 dark:border-white' : 'bg-slate-50 dark:bg-zinc-900 text-slate-600 dark:text-zinc-300 border-slate-200 dark:border-zinc-800'}`} data-testid="journey-mode-auto">Auto-start all</button>
        </div>

        {mode === 'selective' ? (
          <div className="flex-1 overflow-auto p-5">
            <div className="flex items-center justify-between mb-2 text-xs">
              <p className="text-slate-500 dark:text-zinc-400">Picked {selected.length} of {eligible.length}</p>
              <div className="flex gap-2">
                <button onClick={selectAll} className="text-slate-500 dark:text-zinc-400 hover:text-slate-900 dark:hover:text-white" data-testid="journey-select-all">All</button>
                <span className="text-slate-600 dark:text-zinc-700">|</span>
                <button onClick={selectNone} className="text-slate-500 dark:text-zinc-400 hover:text-slate-900 dark:hover:text-white" data-testid="journey-select-none">None</button>
              </div>
            </div>
            {eligible.length === 0 ? (
              <p className="text-slate-500 dark:text-zinc-500 text-sm py-8 text-center">No eligible candidate matches yet.</p>
            ) : eligible.map(m => (
              <label key={m.id} className={`flex items-center gap-3 p-3 rounded-lg border mb-1.5 cursor-pointer ${selected.includes(m.id) ? 'bg-emerald-500/5 border-emerald-500/30' : 'bg-slate-50 dark:bg-[#0a0a0b] border-slate-200 dark:border-zinc-800/70 hover:border-slate-300 dark:hover:border-zinc-700'}`} data-testid={`pick-match-${m.id}`}>
                <input type="checkbox" checked={selected.includes(m.id)} onChange={() => toggle(m.id)} className="accent-emerald-500" />
                <div className="flex-1">
                  <p className="text-slate-900 dark:text-white text-sm">{m.applicant_name || m.applicant_email || 'Candidate'}</p>
                  <p className="text-slate-500 dark:text-zinc-500 text-xs">{Math.round(m.match_percentage || 0)}% match · status {m.status}</p>
                </div>
              </label>
            ))}
          </div>
        ) : (
          <div className="flex-1 p-5">
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-xs text-amber-700 dark:text-amber-300">
              Auto mode will charge {eligible.length * per} MP for {eligible.length} candidate(s) — and may take your balance negative. No further confirmation will be asked per candidate (transactions appear in your Wallet history).
            </div>
          </div>
        )}

        <div className="p-4 border-t border-slate-200 dark:border-zinc-800 flex items-center justify-between bg-slate-50 dark:bg-[#0a0a0b]/70">
          <span className="text-slate-600 dark:text-zinc-300 text-sm">Total: <span className="text-amber-700 dark:text-amber-300 font-semibold">{cost} MP</span></span>
          <button onClick={onConfirm} disabled={mode === 'selective' && selected.length === 0} className="px-4 py-2 bg-slate-900 dark:bg-white text-white dark:text-black rounded-lg text-sm font-medium disabled:opacity-50" data-testid="confirm-start-journeys">
            Review & charge ({cost} MP)
          </button>
        </div>
      </div>
    </div>
  );
}
